<template>
  <div>
    <!-- Search and Sort Bar -->
    <div class="mb-6 flex flex-col sm:flex-row gap-3 w-full">
      <!-- Search Input -->
      <div class="relative flex-1 sm:max-w-[380px]">
        <div
          class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
        >
          <svg
            class="h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            ></path>
          </svg>
        </div>
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          :placeholder="searchPlaceholder"
          class="block w-full pl-10 pr-3 py-2 border border-gray-300 focus:outline-none rounded-lg text-sm focus:ring-brand-500 focus:border-brand-500"
          @input="handleSearch"
        />
      </div>

      <!-- Sort Dropdown -->
      <select
        v-model="sortOrder"
        @change="handleSortChange"
        class="sm:w-[160px] pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 bg-white appearance-none bg-no-repeat bg-right text-gray-900"
        style="
          background-image: url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3E%3Cpath stroke=%27%236B7280%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27M6 8l4 4 4-4%27/%3E%3C/svg%3E');
          background-position: right 0.5rem center;
          background-size: 1.25rem 1.25rem;
        "
      >
        <option value="a-z">
          <Translation name="facilities.sort_a_z" default="Sort A-Z" />
        </option>
        <option value="z-a">
          <Translation name="facilities.sort_z_a" default="Sort Z-A" />
        </option>
      </select>
    </div>

    <!-- Facilities Grid -->
    <div
      v-if="filteredFacilities.length > 0"
      class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 gap-x-5"
    >
      <a
        v-for="facility in filteredFacilities"
        :key="facility.id"
        :href="facilityUrl(facility.id)"
        class="block"
      >
        <div
          class="bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all flex flex-col h-full"
        >
          <div
            :style="`background-image: url('${facilityCardHeaderImage}')`"
            class="rounded-t-xl bg-cover bg-center h-16"
          ></div>
          <div class="px-6 pb-6 pt-2 flex flex-col h-full">
            <!-- Country Flag and Name -->
            <div v-if="facility.country" class="flex items-center mb-4">
              <div class="mr-2 flex-shrink-0">
                <div
                  class="rounded-full bg-white border border-gray-200 flex items-center justify-center overflow-hidden shadow-sm"
                  style="width: 24px; height: 24px; min-width: 24px; min-height: 24px;"
                >
                  <span
                    :class="`fi fi-${facility.country.toLowerCase()}`"
                    style="font-size: 24px;"
                  ></span>
                </div>
              </div>
              <span class="text-base font-semibold text-gray-900">
                {{ facility.country_name }}
              </span>
            </div>

            <!-- Facility Name -->
            <h3 class="text-xl font-bold text-gray-900 mb-4">
              {{ facility.name }}
            </h3>

            <!-- Sector Info -->
            <div class="flex items-center mb-2">
              <div class="mr-2 flex-shrink-0">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.6667 13.2734V7.94009H8.00001H3.33334V13.2734M1.33334 13.2734H14.6667V5.27342L8.35246 2.81064C8.32711 2.80075 8.31444 2.79581 8.30239 2.79036C8.29104 2.78523 8.28175 2.78062 8.27079 2.7747C8.25915 2.76842 8.24577 2.76024 8.21901 2.7439C8.15619 2.70554 8.11613 2.68439 8.07387 2.67479C8.02525 2.66374 7.97477 2.66374 7.92615 2.67479C7.88389 2.68439 7.84383 2.70554 7.78101 2.7439C7.75425 2.76024 7.74087 2.76842 7.72923 2.7747C7.71828 2.78062 7.70898 2.78523 7.69764 2.79036C7.68558 2.79581 7.67291 2.80075 7.64756 2.81064L1.33334 5.27342V13.2734Z" stroke="#697586" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <span class="text-sm text-gray-500">
                {{ facility.sector_name }}
              </span>
            </div>

            <!-- Products -->
            <div v-if="facility.products_display" class="flex items-center">
              <div class="mr-2 flex-shrink-0">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.6666 4.85173L7.99998 7.99987M7.99998 7.99987L2.33331 4.85173M7.99998 7.99987L8 14.3332M14 10.7056V5.29419C14 5.06576 14 4.95155 13.9663 4.84968C13.9366 4.75956 13.8879 4.67684 13.8236 4.60705C13.7509 4.52816 13.651 4.47269 13.4514 4.36176L8.51802 1.62102C8.32895 1.51598 8.23442 1.46346 8.1343 1.44287C8.0457 1.42464 7.95431 1.42464 7.8657 1.44287C7.76559 1.46346 7.67105 1.51598 7.48198 1.62102L2.54865 4.36176C2.34896 4.47269 2.24912 4.52816 2.17642 4.60705C2.11211 4.67684 2.06343 4.75957 2.03366 4.84968C2 4.95155 2 5.06576 2 5.29419V10.7056C2 10.934 2 11.0482 2.03366 11.1501C2.06343 11.2402 2.11211 11.3229 2.17642 11.3927C2.24912 11.4716 2.34897 11.5271 2.54865 11.638L7.48198 14.3788C7.67105 14.4838 7.76559 14.5363 7.8657 14.5569C7.95431 14.5751 8.0457 14.5751 8.1343 14.5569C8.23442 14.5363 8.32895 14.4838 8.51802 14.3788L13.4514 11.638C13.651 11.5271 13.7509 11.4716 13.8236 11.3927C13.8879 11.3229 13.9366 11.2402 13.9663 11.1501C14 11.0482 14 10.934 14 10.7056Z" stroke="#697586" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M11 6.33333L5 3" stroke="#697586" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <span class="text-sm text-gray-500">
                {{ facility.products_display }}
              </span>
            </div>

            <!-- Footer with Facility ID and Linked -->
            <div class="mt-auto pt-4 border-t border-gray-200 grid grid-cols-2">
              <div>
                <div class="text-sm text-gray-500 mb-1">
                  <Translation
                    name="facilities.facility_id"
                    default="Facility ID"
                  />
                </div>
                <div
                  class="text-sm font-semibold text-gray-500 px-1 bg-gray-100 rounded-sm w-fit"
                >
                  {{ facility.facility_id }}
                </div>
              </div>
              <div>
                <div class="text-sm text-gray-500 mb-1">
                  <Translation name="facilities.linked" default="Linked" />
                </div>
                <div
                  class="flex items-center gap-0.5 text-sm font-semibold text-gray-500 px-2 bg-gray-100 rounded-sm w-fit"
                >
                  {{ facility.calculations_count }}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.3333 6.3335V8.60016C11.3333 8.97353 11.3333 9.16022 11.2607 9.30282C11.1968 9.42827 11.0948 9.53025 10.9693 9.59417C10.8267 9.66683 10.64 9.66683 10.2667 9.66683L2 9.66683M8.66667 9.66683V11.9335C8.66667 12.3069 8.66667 12.4935 8.594 12.6362C8.53009 12.7616 8.4281 12.8636 8.30266 12.9275C8.16005 13.0002 7.97337 13.0002 7.6 13.0002H2M2 1.3335L2 14.6668M2 6.3335L12.9333 6.3335C13.3067 6.3335 13.4934 6.3335 13.636 6.26083C13.7614 6.19692 13.8634 6.09493 13.9273 5.96949C14 5.82688 14 5.6402 14 5.26683V4.06683C14 3.69346 14 3.50678 13.9273 3.36417C13.8634 3.23873 13.7614 3.13674 13.636 3.07283C13.4934 3.00016 13.3067 3.00016 12.9333 3.00016L2 3.00016L2 6.3335Z" stroke="#697586" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </a>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-12">
      <div
        class="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4"
      >
        <svg
          class="w-12 h-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          ></path>
        </svg>
      </div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">
        <Translation
          v-if="isFiltered"
          name="facilities.no_facilities_found"
          default="No facilities found"
        />
        <Translation
          v-else
          name="facilities.no_facilities"
          default="No facilities yet"
        />
      </h3>
      <p v-if="isFiltered" class="text-gray-500 mb-6">
        <Translation
          name="facilities.no_facilities_found_description"
          default="Try adjusting your search to find what you're looking for."
        />
      </p>
      <p v-else class="text-gray-500 mb-6">
        <Translation
          name="facilities.no_facilities_description"
          default="Get started by creating your first facility to begin tracking wage gaps and improvements."
        />
      </p>
      <a
        v-if="!isFiltered"
        :href="newFacilityUrl"
        class="bg-brand-600 text-white text-sm font-semibold rounded-lg px-4 py-2 inline-flex items-center hover:bg-brand-700 transition-colors"
      >
        <svg
          class="w-4 h-4 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4"
          ></path>
        </svg>
        <Translation
          name="facilities.create_first_facility"
          default="Create Your First Facility"
        />
      </a>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import Translation from "./aft/Translation.vue";
import { useTranslation } from "./aft/useTranslations";

export default {
  components: { Translation },
  name: "FacilitiesList",
  props: {
    facilities: {
      type: Array,
      required: true,
    },
    locale: {
      type: String,
      required: true,
    },
    searchParam: {
      type: String,
      default: "",
    },
    sortParam: {
      type: String,
      default: "a-z",
    },
    facilityCardHeaderImage: {
      type: String,
      required: true,
    },
    newFacilityUrl: {
      type: String,
      required: true,
    },
    enableUrlSync: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const searchQuery = ref(props.searchParam);
    const sortOrder = ref(props.sortParam);
    const { _t: translate } = useTranslation();

    const searchPlaceholder = translate(
      "facilities.search_placeholder",
      "Search by facility name or ID..."
    );

    const isFiltered = computed(
      () => filteredFacilities.value.length !== props.facilities.length
    );

    const filteredFacilities = computed(() => {
      let results = [...props.facilities];

      // Apply search filter
      if (searchQuery.value && searchQuery.value.trim() !== "") {
        const search = searchQuery.value.toLowerCase().trim();
        results = results.filter((facility) => {
          const facilityName = facility.name?.toLowerCase() || "";
          const facilityId = (facility.facility_id || "").toLowerCase();

          return facilityName.includes(search) || facilityId.includes(search);
        });
      }

      // Apply sort
      results.sort((a, b) => {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();

        if (sortOrder.value === "a-z") {
          return nameA.localeCompare(nameB);
        } else {
          return nameB.localeCompare(nameA);
        }
      });

      return results;
    });

    const updateUrl = () => {
      if (!props.enableUrlSync) {
        return;
      }

      const params = new URLSearchParams();

      if (searchQuery.value && searchQuery.value.trim() !== "") {
        params.set("search", searchQuery.value);
      }

      if (sortOrder.value !== "a-z") {
        params.set("sort", sortOrder.value);
      }

      const queryString = params.toString();
      const newUrl = queryString
        ? `/${props.locale}/facilities?${queryString}`
        : `/${props.locale}/facilities`;

      window.history.pushState({}, "", newUrl);
    };

    let searchTimeout = null;
    const handleSearch = () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      // Debounce URL update but filter immediately (real-time filtering)
      searchTimeout = setTimeout(() => {
        updateUrl();
      }, 500);
    };

    const handleSortChange = () => {
      updateUrl();
    };

    const facilityUrl = (id) => {
      return `/${props.locale}/facilities/${id}`;
    };

    return {
      searchQuery,
      sortOrder,
      searchPlaceholder,
      filteredFacilities,
      isFiltered,
      handleSearch,
      handleSortChange,
      facilityUrl,
      translate,
    };
  },
};
</script>
